import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaChartLine,
  FaInstagram,
  FaShieldAlt,
  FaTelegramPlane,
  FaTiktok,
  FaWhatsapp,
} from "react-icons/fa";
import { FaBoltLightning } from "react-icons/fa6";
import { collection, doc, getDocs, query, setDoc, where } from "firebase/firestore";
import { db } from "../firebase";

const LEGACY_APP_ID = "133411";
const DERIV_CLIENT_ID = "339JNIkO5fCNmxYblapOF";
const DERIV_REDIRECT_URL = "https://www.matchespro.site/";
const DERIV_OAUTH_URL = "https://auth.deriv.com/oauth2/auth";
const DERIV_TOKEN_URL = "https://auth.deriv.com/oauth2/token";
const DERIV_OPTIONS_ACCOUNTS_URL = "https://api.derivws.com/trading/v1/options/accounts";
const DERIV_OAUTH_SCOPE = "trade";
const DERIV_WS_URL = `wss://ws.binaryws.com/websockets/v3?app_id=${LEGACY_APP_ID}`;
const DERIV_PKCE_VERIFIER_KEY = "derivPkceVerifier";
const DERIV_OAUTH_STATE_KEY = "derivOAuthState";

const socialLinks = [
  { label: "Telegram", href: "https://t.me/matchespro", icon: FaTelegramPlane },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@profitmakertool?_r=1&_t=ZS-95oLSw4DVxo",
    icon: FaTiktok,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/markethunter.site?igsh=MWx0NXNxa3ljOXZzaA%3D%3D&utm_source=qr",
    icon: FaInstagram,
  },
  {
    label: "WhatsApp",
    href: "https://chat.whatsapp.com/BiM2rqbgAzJ1yBOE5h6E8p",
    icon: FaWhatsapp,
  },
];

const normalizeEmail = (email) => email?.trim().toLowerCase() || "";

const firstNonEmptyString = (...values) =>
  values.find((value) => typeof value === "string" && value.trim().length > 0) || "";

const base64UrlEncode = (buffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");

const createRandomString = (byteLength = 32) => {
  const randomValues = new Uint8Array(byteLength);
  window.crypto.getRandomValues(randomValues);
  return base64UrlEncode(randomValues.buffer);
};

const createPkceChallenge = async (verifier) => {
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(digest);
};

const hasOAuthCallbackParams = (params) =>
  params.has("acct1") || params.has("token1") || params.has("code") || params.has("error");

const buildCallbackError = (params) => {
  const error = params.get("error");
  if (!error) {
    return null;
  }

  const description = params.get("error_description");
  return new Error(description || error || "Deriv authentication was cancelled");
};

const exchangeAuthorizationCode = async (code, verifier) => {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: DERIV_CLIENT_ID,
    code,
    code_verifier: verifier,
    redirect_uri: DERIV_REDIRECT_URL,
  });

  const response = await fetch(DERIV_TOKEN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error_description || payload.error || "Failed to exchange Deriv authorization code");
  }

  if (!payload.access_token) {
    throw new Error("Deriv token response did not include an access token");
  }

  return payload.access_token;
};

const startDerivOAuth = async ({ prompt } = {}) => {
  const verifier = createRandomString(48);
  const state = createRandomString(24);
  const challenge = await createPkceChallenge(verifier);

  sessionStorage.setItem(DERIV_PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(DERIV_OAUTH_STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: DERIV_CLIENT_ID,
    redirect_uri: DERIV_REDIRECT_URL,
    scope: DERIV_OAUTH_SCOPE,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
    app_id: LEGACY_APP_ID,
  });

  if (prompt) {
    params.set("prompt", prompt);
  }

  window.location.href = `${DERIV_OAUTH_URL}?${params.toString()}`;
};

const fetchDerivProfile = (token) =>
  new Promise((resolve, reject) => {
    if (!token) {
      resolve(null);
      return;
    }

    const ws = new WebSocket(DERIV_WS_URL);
    const timeout = window.setTimeout(() => {
      try {
        ws.close();
      } catch {}
      reject(new Error("Timed out while fetching Deriv profile"));
    }, 10000);

    ws.onopen = () => {
      ws.send(JSON.stringify({ authorize: token }));
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);

        if (payload.error) {
          clearTimeout(timeout);
          ws.close();
          reject(new Error(payload.error.message || "Deriv authorization failed"));
          return;
        }

        if (!payload.authorize) return;

        const { authorize } = payload;
        clearTimeout(timeout);
        ws.close();
        resolve({
          loginid: authorize.loginid || authorize.login_id || "",
          email: authorize.email || authorize.email_address || "",
          name:
            authorize.fullname ||
            authorize.full_name ||
            authorize.name ||
            authorize.email ||
            authorize.loginid ||
            "",
          accountList: Array.isArray(authorize.account_list) ? authorize.account_list : [],
          rawAuthorize: authorize,
        });
      } catch (error) {
        clearTimeout(timeout);
        ws.close();
        reject(error);
      }
    };

    ws.onerror = () => {
      clearTimeout(timeout);
      try {
        ws.close();
      } catch {}
      reject(new Error("Failed to connect to Deriv"));
    };
  });

const fetchDerivOptionsAccounts = async (token) => {
  if (!token) {
    return [];
  }

  const response = await fetch(DERIV_OPTIONS_ACCOUNTS_URL, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Deriv-App-ID": DERIV_CLIENT_ID,
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      payload?.errors?.[0]?.message ||
        payload?.message ||
        "Failed to fetch Deriv trading accounts"
    );
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (payload?.data && typeof payload.data === "object") {
    return [payload.data];
  }

  return [];
};

const resolveDerivIdentity = ({
  fallbackLoginid = "",
  profile = null,
  optionAccounts = [],
} = {}) => {
  const accountList = Array.isArray(profile?.accountList) ? profile.accountList : [];
  const matchingAccount =
    fallbackLoginid &&
    accountList.find((account) =>
      [
        account?.loginid,
        account?.login_id,
        account?.account,
        account?.account_id,
        account?.accountId,
      ].includes(fallbackLoginid)
    );
  const firstAccount = matchingAccount || accountList[0] || null;
  const matchingOptionAccount =
    fallbackLoginid &&
    optionAccounts.find((account) =>
      [account?.account_id, account?.accountId].includes(fallbackLoginid)
    );
  const firstOptionAccount = matchingOptionAccount || optionAccounts[0] || null;

  const loginid = firstNonEmptyString(
    fallbackLoginid,
    profile?.loginid,
    profile?.rawAuthorize?.loginid,
    profile?.rawAuthorize?.login_id,
    matchingAccount?.loginid,
    matchingAccount?.login_id,
    matchingAccount?.account,
    matchingAccount?.account_id,
    matchingAccount?.accountId,
    firstAccount?.loginid,
    firstAccount?.login_id,
    firstAccount?.account,
    firstAccount?.account_id,
    firstAccount?.accountId,
    profile?.rawAuthorize?.account,
    profile?.rawAuthorize?.account_id,
    profile?.rawAuthorize?.accountId,
    matchingOptionAccount?.account_id,
    matchingOptionAccount?.accountId,
    firstOptionAccount?.account_id,
    firstOptionAccount?.accountId
  );

  const rawEmail = firstNonEmptyString(
    profile?.email,
    profile?.rawAuthorize?.email,
    profile?.rawAuthorize?.email_address
  );

  const name = firstNonEmptyString(profile?.name, profile?.rawAuthorize?.fullname, rawEmail, loginid);

  return {
    loginid,
    rawEmail,
    email: normalizeEmail(rawEmail),
    name,
  };
};

const findExistingUserDoc = async (usersCol, loginid, email) => {
  if (loginid) {
    const loginQuery = query(usersCol, where("loginid", "==", loginid));
    const loginSnapshot = await getDocs(loginQuery);
    if (!loginSnapshot.empty) {
      return loginSnapshot.docs[0];
    }
  }

  const normalizedEmail = normalizeEmail(email);
  const emailCandidates = Array.from(new Set([email, normalizedEmail].filter(Boolean)));

  if (!emailCandidates.length) {
    return null;
  }

  for (const emailCandidate of emailCandidates) {
    const emailQuery = query(usersCol, where("email", "==", emailCandidate));
    const emailSnapshot = await getDocs(emailQuery);
    if (!emailSnapshot.empty) {
      return emailSnapshot.docs[0];
    }
  }

  return null;
};

export default function LoginDeriv() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [authError, setAuthError] = useState("");
  const [profileSetup, setProfileSetup] = useState(null);
  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    const handleOAuth = async () => {
      const params = new URLSearchParams(location.search);
      if (!hasOAuthCallbackParams(params)) return;

      setAuthError("");

      try {
        const callbackError = buildCallbackError(params);
        if (callbackError) {
          throw callbackError;
        }

        const isLegacyCallback = params.has("token1") || params.has("acct1");
        let loginid = params.get("acct1") || "";
        let token = params.get("token1") || "";

        if (params.has("code")) {
          const returnedState = params.get("state");
          const storedState = sessionStorage.getItem(DERIV_OAUTH_STATE_KEY);
          const verifier = sessionStorage.getItem(DERIV_PKCE_VERIFIER_KEY);

          if (!storedState || !verifier) {
            throw new Error("Deriv login session expired. Please try again.");
          }

          if (!returnedState || returnedState !== storedState) {
            throw new Error("Deriv login validation failed. Please try again.");
          }

          token = await exchangeAuthorizationCode(params.get("code"), verifier);

          sessionStorage.removeItem(DERIV_OAUTH_STATE_KEY);
          sessionStorage.removeItem(DERIV_PKCE_VERIFIER_KEY);
        }

        if (!loginid && !token) {
          throw new Error("Deriv did not return a valid login session.");
        }

        const usersCol = collection(db, "deriv_users");

        let profile = null;
        let optionAccounts = [];
        if (token && isLegacyCallback) {
          try {
            profile = await fetchDerivProfile(token);
          } catch (profileError) {
            console.warn("[LoginDeriv] Could not fetch full Deriv profile:", profileError);
          }
        }

        if (token) {
          try {
            optionAccounts = await fetchDerivOptionsAccounts(token);
          } catch (accountsError) {
            console.warn("[LoginDeriv] Could not fetch Deriv options accounts:", accountsError);
          }
        }

        const identity = resolveDerivIdentity({
          fallbackLoginid: loginid,
          profile,
          optionAccounts,
        });
        loginid = identity.loginid;
        if (!loginid) {
          throw new Error("Unable to resolve your Deriv account ID.");
        }

        const resolvedEmail = identity.email || "";
        const resolvedName = identity.rawEmail
          ? identity.name?.trim() || ""
          : profile?.name?.trim() || "";
        const existingDoc = await findExistingUserDoc(
          usersCol,
          loginid,
          identity.rawEmail || resolvedEmail
        );
        const existingData = existingDoc?.data() || {};
        const timestamp = new Date().toISOString();

        if (existingDoc) {
          await setDoc(
            existingDoc.ref,
            {
              loginid,
              email: resolvedEmail || existingData.email || "",
              name: resolvedName || existingData.name || "",
              lastLoginAt: timestamp,
            },
            { merge: true }
          );
        } else {
          await setDoc(doc(usersCol, loginid), {
            loginid,
            email: resolvedEmail,
            name: resolvedName,
            status: "pending",
            approved: false,
            createdAt: timestamp,
            lastLoginAt: timestamp,
          });
        }

        localStorage.setItem("currentDerivLogin", loginid);
        const effectiveEmail = resolvedEmail || existingData.email || "";
        const effectiveName = resolvedName || existingData.name || "";
        const approved = existingData.approved ?? false;
        const userDocId = existingDoc?.id || loginid;

        if (!effectiveEmail || !effectiveName) {
          setProfileSetup({
            userDocId,
            redirectTo: approved ? "/user" : "/pending",
          });
          setProfileEmail(effectiveEmail);
          setProfileName(effectiveName);
          setCheckingAuth(false);
          window.history.replaceState({}, document.title, location.pathname);
          return;
        }

        navigate(approved ? "/user" : "/pending", { replace: true });
      } catch (err) {
        console.error("[LoginDeriv] OAuth flow failed:", err);
        sessionStorage.removeItem(DERIV_OAUTH_STATE_KEY);
        sessionStorage.removeItem(DERIV_PKCE_VERIFIER_KEY);
        setAuthError(err.message || "Unable to complete Deriv sign in.");
        setCheckingAuth(false);
      }
    };

    handleOAuth();
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const checkSession = async () => {
      const sessionLoginid = localStorage.getItem("currentDerivLogin");
      if (!sessionLoginid) {
        setCheckingAuth(false);
        return;
      }

      try {
        const usersCol = collection(db, "deriv_users");
        const q = query(usersCol, where("loginid", "==", sessionLoginid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          setCheckingAuth(false);
          return;
        }

        const { approved } = querySnapshot.docs[0].data();
        navigate(approved ? "/user" : "/pending", { replace: true });
      } catch (err) {
        console.error("[LoginDeriv] Auth check failed:", err);
        setCheckingAuth(false);
      }
    };

    if (profileSetup) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (!hasOAuthCallbackParams(params)) {
      checkSession();
    }
  }, [location.search, navigate, profileSetup]);

  const handleProfileSetup = async (event) => {
    event.preventDefault();

    const trimmedName = profileName.trim();
    const normalizedProfileEmail = normalizeEmail(profileEmail);

    if (!trimmedName) {
      setAuthError("Enter your name to continue.");
      return;
    }

    if (!normalizedProfileEmail) {
      setAuthError("Enter your email to continue.");
      return;
    }

    setSavingProfile(true);
    setAuthError("");

    try {
      await setDoc(
        doc(db, "deriv_users", profileSetup.userDocId),
        {
          name: trimmedName,
          email: normalizedProfileEmail,
          profileCompletedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      navigate(profileSetup.redirectTo, { replace: true });
    } catch (error) {
      console.error("[LoginDeriv] Failed to save onboarding details:", error);
      setAuthError("Unable to save your details right now.");
      setSavingProfile(false);
    }
  };

  const handleDerivLogin = async () => {
    try {
      setAuthError("");
      await startDerivOAuth();
    } catch (error) {
      console.error("[LoginDeriv] Failed to start Deriv login:", error);
      setAuthError("Unable to start Deriv sign in right now.");
    }
  };

  const handleDerivSignup = async () => {
    try {
      setAuthError("");
      await startDerivOAuth({ prompt: "registration" });
    } catch (error) {
      console.error("[LoginDeriv] Failed to start Deriv signup:", error);
      setAuthError("Unable to start Deriv account creation right now.");
    }
  };

  if (checkingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0f1514] text-slate-300">
        Checking authentication...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1514] text-white">
      <div className="mx-auto grid min-h-screen max-w-7xl lg:grid-cols-[1.05fr_0.95fr]">
        <section className="flex items-center px-6 py-14 lg:px-12">
          <div className="max-w-xl">
            <p className="inline-flex rounded-full border border-[#819C97]/40 bg-[#819C97]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#819C97]">
              Matches Pro access
            </p>
            <h1 className="mt-6 text-4xl font-black leading-tight md:text-5xl">
              Sign in with Deriv to enter the Matches Pro workflow.
            </h1>
            <p className="mt-5 text-lg leading-8 text-slate-300">
              Your Deriv login stays with Deriv. Matches Pro uses the updated app connection and sends new users into the approval and payment flow automatically.
            </p>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <Feature icon={<FaChartLine />} title="Matches-first signals" text="Focused on a dedicated Matches Pro experience instead of mixed contract messaging." />
              <Feature icon={<FaBoltLightning />} title="Live timing windows" text="Use confidence and entry timing to approach setups with more structure." />
              <Feature icon={<FaShieldAlt />} title="OAuth security" text="No password storage. Authentication happens through Deriv directly." />
            </div>

            <div className="mt-10 rounded-[1.75rem] border border-white/10 bg-white/5 p-6">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#819C97]">Official community links</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {socialLinks.map(({ label, href, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 transition hover:border-[#A87F23]/50 hover:text-[#A87F23]"
                  >
                    <Icon />
                    {label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="flex items-center justify-center bg-[#131b1a] px-6 py-14 lg:px-12">
          <div className="w-full max-w-md rounded-[2rem] border border-white/10 bg-[#17201f] p-8 shadow-2xl shadow-black/30">
            <div className="mb-6 flex items-center gap-3">
              <img src="/logo.jpeg" alt="Matches Pro" className="h-12 w-12 rounded-full border border-white/10 object-cover" />
              <div>
                <p className="text-lg font-black tracking-[0.15em] text-[#A87F23]">MATCHES PRO</p>
                <p className="text-sm text-slate-400">Secure Deriv sign-in</p>
              </div>
            </div>

            {profileSetup ? (
              <form onSubmit={handleProfileSetup}>
                <div className="rounded-[1.5rem] border border-[#819C97]/30 bg-[#0f1514] p-5">
                  <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#819C97]">Complete your profile</p>
                  <div className="mt-4 space-y-3 text-sm text-slate-300">
                    <p>Add your real name and email once so your Matches Pro account can be identified correctly.</p>
                  </div>

                  <div className="mt-5 space-y-4">
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-slate-200">Your name</span>
                      <input
                        type="text"
                        value={profileName}
                        onChange={(event) => setProfileName(event.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-[#17201f] px-4 py-3 text-white placeholder-slate-500 focus:border-[#819C97] focus:outline-none"
                        placeholder="Enter your full name"
                        autoComplete="name"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-slate-200">Your email</span>
                      <input
                        type="email"
                        value={profileEmail}
                        onChange={(event) => setProfileEmail(event.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-[#17201f] px-4 py-3 text-white placeholder-slate-500 focus:border-[#819C97] focus:outline-none"
                        placeholder="Enter your email"
                        autoComplete="email"
                      />
                    </label>
                  </div>
                </div>

                {authError ? (
                  <div className="mt-6 rounded-[1.5rem] border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                    {authError}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={savingProfile}
                  className="mt-8 w-full rounded-full bg-[#A87F23] py-4 text-lg font-bold text-[#0f1514] transition hover:bg-[#c19432] disabled:opacity-60"
                >
                  {savingProfile ? "Saving..." : "Continue"}
                </button>
              </form>
            ) : (
              <>
                <div className="rounded-[1.5rem] border border-[#819C97]/30 bg-[#0f1514] p-5">
                  <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#819C97]">What happens next</p>
                  <div className="mt-4 space-y-3 text-sm text-slate-300">
                    <p>1. Sign in or create your Deriv account.</p>
                    <p>2. Complete Matches Pro payment confirmation.</p>
                    <p>3. Access the live dashboard once approved.</p>
                  </div>
                </div>

                {authError ? (
                  <div className="mt-6 rounded-[1.5rem] border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                    {authError}
                  </div>
                ) : null}

                <button
                  onClick={handleDerivLogin}
                  className="mt-8 w-full rounded-full bg-[#A87F23] py-4 text-lg font-bold text-[#0f1514] transition hover:bg-[#c19432]"
                >
                  Continue with Deriv
                </button>

                <button
                  onClick={handleDerivSignup}
                  className="mt-4 w-full rounded-full border border-[#819C97]/40 bg-transparent py-4 text-base font-semibold text-slate-200 transition hover:border-[#A87F23]/50 hover:text-[#A87F23]"
                >
                  Create a new Deriv account
                </button>

                <p className="mt-5 text-center text-sm leading-6 text-slate-400">
                  Matches Pro price: $200 USD for matches access.
                </p>
              </>
            )}

            <div className="mt-6 border-t border-white/10 pt-6 text-center">
              <Link to="/" className="text-sm font-semibold text-[#819C97] transition hover:text-[#A87F23]">
                Back to Matches Pro home
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

const Feature = ({ icon, title, text }) => (
  <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-4">
    <div className="text-lg text-[#819C97]">{icon}</div>
    <h4 className="mt-3 font-semibold text-white">{title}</h4>
    <p className="mt-2 text-sm leading-6 text-slate-400">{text}</p>
  </div>
);
